/**
 * ProductImageGallery — drag-and-drop multi-image uploader for the Product form.
 *
 * Behavior (Feb 2026 multi-image upgrade):
 *  - Up to MAX_GALLERY (6) images per product.
 *  - First image in the array is always the "Primary" — the legacy `product_image`
 *    field on the product is auto-synced to gallery[0].url server-side.
 *  - For NEW products (no productId yet): files are queued in local state, and the
 *    parent form uploads them after the product is created.
 *  - For EXISTING products (productId given): uploads / deletes are persisted
 *    immediately via /api/admin/products/{id}/images endpoints.
 *  - Drag-and-drop OR click-to-pick. Thumbnails have an X to remove individually.
 *  - No re-ordering (per user spec — first uploaded = primary).
 */
import React, { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ImagePlus, Trash2, UploadCloud, Loader2, Image as ImageIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_GALLERY = 6;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024; // 5 MB

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const resolveUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://') || u.startsWith('data:')) return u;
  return `${BACKEND_URL}${u}`;
};

const ProductImageGallery = ({
  productId,           // string | null — null for "create new product" mode
  gallery,             // [{id, url, alt}] — saved gallery for existing products
  setGallery,          // setter for gallery
  pendingFiles,        // [{key, file, previewUrl}] — queued for new products
  setPendingFiles,     // setter for pendingFiles
  setFormData,         // parent formData setter — mirrors the freshly-uploaded
                       // primary URL so the outer PUT /api/products/{id} does
                       // NOT clobber it with a stale product_image value.
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const totalCount = (gallery?.length || 0) + (pendingFiles?.length || 0);
  const remaining = Math.max(0, MAX_GALLERY - totalCount);

  const validateFiles = (files) => {
    const list = Array.from(files || []);
    const ok = [];
    for (const f of list) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        toast.error(`${f.name}: only JPEG/PNG/WEBP allowed`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name}: exceeds 5 MB`);
        continue;
      }
      ok.push(f);
    }
    if (ok.length + totalCount > MAX_GALLERY) {
      toast.error(`Gallery limit is ${MAX_GALLERY} — only the first ${remaining} will be added`);
      return ok.slice(0, remaining);
    }
    return ok;
  };

  const queueForNewProduct = (files) => {
    const queued = files.map((file) => ({
      key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPendingFiles([...(pendingFiles || []), ...queued]);
  };

  const uploadToExisting = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const form = new FormData();
      files.forEach((f) => form.append('files', f));
      const { data } = await axios.post(
        `${API}/admin/products/${productId}/images`, form,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } },
      );
      setGallery(data.product_images || []);
      // CRITICAL: mirror the freshly-persisted primary URL into the parent
      // form state. Otherwise the very next "Update Product" PUT sends the
      // stale product_image the dialog opened with and clobbers this write.
      // (This is the bug that caused ~40 Master Catalogue uploads to vanish
      // from db.products.product_image on production for weeks.)
      if (setFormData) {
        setFormData((prev) => ({ ...prev, product_image: data.product_image || '' }));
      }
      toast.success(`${files.length} image(s) uploaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = useCallback(async (rawFiles) => {
    const files = validateFiles(rawFiles);
    if (!files.length) return;
    if (productId) {
      await uploadToExisting(files);
    } else {
      queueForNewProduct(files);
    }
  }, [productId, totalCount, gallery, pendingFiles]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
  };

  const onPick = (e) => {
    // Capture files as an array BEFORE clearing the input — `input.value = ''`
    // clears the LIVE FileList, so a reference held in a const would also empty.
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length) handleFiles(files);
  };

  const removePending = (key) => {
    const item = pendingFiles.find((p) => p.key === key);
    if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
    setPendingFiles(pendingFiles.filter((p) => p.key !== key));
  };

  const removeSaved = async (imgId) => {
    if (!productId) return;
    setUploading(true);
    try {
      const { data } = await axios.delete(
        `${API}/admin/products/${productId}/images/${imgId}`,
        { headers: authHeaders() },
      );
      setGallery(data.product_images || []);
      // Mirror the new primary URL (or '' if gallery is now empty) so the
      // parent PUT doesn't resurrect the removed image from stale formData.
      if (setFormData) {
        setFormData((prev) => ({ ...prev, product_image: data.product_image || '' }));
      }
      toast.success('Image removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    } finally {
      setUploading(false);
    }
  };

  // Render order: saved gallery first (gallery[0] is primary), then pending queue.
  const tiles = [
    ...(gallery || []).map((g, idx) => ({
      kind: 'saved', key: `s-${g.id}`, id: g.id, url: resolveUrl(g.url),
      isPrimary: idx === 0, alt: g.alt || '',
    })),
    ...(pendingFiles || []).map((p, idx) => ({
      kind: 'pending', key: `p-${p.key}`, pendKey: p.key, url: p.previewUrl,
      isPrimary: (gallery?.length || 0) === 0 && idx === 0, alt: p.file?.name || '',
    })),
  ];

  return (
    <div className="space-y-2" data-testid="product-image-gallery">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">
          Product Images <span className="text-slate-400 font-normal">({totalCount}/{MAX_GALLERY})</span>
        </label>
        {!productId && pendingFiles?.length > 0 && (
          <Badge variant="outline" className="text-[10px]">
            Will upload after product is saved
          </Badge>
        )}
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed rounded-lg p-4 transition-colors ${
          dragOver ? 'border-orange-500 bg-orange-50' : 'border-slate-300 bg-slate-50'
        } ${remaining === 0 ? 'opacity-60' : ''}`}
        data-testid="product-image-gallery-dropzone"
      >
        {tiles.length === 0 ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || remaining === 0}
            className="w-full flex flex-col items-center justify-center py-6 text-slate-500 hover:text-orange-600 transition"
            data-testid="product-image-gallery-empty-pick"
          >
            <UploadCloud className="w-8 h-8 mb-2" />
            <p className="text-sm font-medium">Drag &amp; drop images here</p>
            <p className="text-xs text-slate-400">or click to browse — JPEG/PNG/WEBP, up to {MAX_GALLERY} images, 5 MB each</p>
          </button>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {tiles.map((t) => (
              <div
                key={t.key}
                className="relative group aspect-square border rounded-md overflow-hidden bg-white"
                data-testid={`product-image-tile-${t.kind}`}
              >
                <img src={t.url} alt={t.alt} className="w-full h-full object-cover" />
                {t.isPrimary && (
                  <span className="absolute top-1 left-1 text-[9px] uppercase font-semibold bg-orange-600 text-white px-1.5 py-0.5 rounded">
                    Primary
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => (t.kind === 'pending' ? removePending(t.pendKey) : removeSaved(t.id))}
                  disabled={uploading}
                  className="absolute top-1 right-1 bg-rose-600 hover:bg-rose-700 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition disabled:opacity-30"
                  data-testid={`product-image-remove-${t.kind === 'pending' ? t.pendKey : t.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
            {remaining > 0 && (
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={uploading}
                className="aspect-square border-2 border-dashed border-slate-300 hover:border-orange-500 hover:bg-orange-50 rounded-md flex flex-col items-center justify-center text-slate-400 hover:text-orange-600 transition"
                data-testid="product-image-gallery-add-tile"
              >
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
                <span className="text-[10px] mt-1">Add</span>
              </button>
            )}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          onChange={onPick}
          className="hidden"
          data-testid="product-image-gallery-file-input"
        />
      </div>

      {tiles.length > 0 && (
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <ImageIcon className="w-3 h-3" />
          First image is the primary product image shown on the OG App tile and exports.
        </div>
      )}
    </div>
  );
};

export default ProductImageGallery;
